import { useCallback } from "react";
import { gql, useMutation } from "@apollo/client";
import { trackEvent } from "../telemetry/appInsights";

// Mirrors providerPortalFrontend/frontend/src/hooks/useMemberAssistantSend.ts
// in shape, but runs the mutation through our app-level Apollo client
// (Member360ApolloProvider) rather than astrana-ui's GraphQLProvider. The
// astrana-ui client is only wired up inside <MemberProfile>'s subtree, so a
// hook called in the page wrapper falls through to its fallback client —
// which points at localhost:4000 and produced a connection-refused on the
// first send. Our app client is always in scope for MemberProfilePage, is
// configured for VITE_CARE_MANAGEMENT_GQL_ENDPOINT, and already attaches
// the bearer via authLink.
//
// The returned handler is structurally compatible with astrana-ui's
// AssistantSendHandler type (args: { message, history, memberId?, memberName? }
// => Promise<string>). Those types aren't in the package's public exports, so
// we re-state the minimal args shape here.

const MEMBER_360_AI_CHAT_MUTATION = gql`
  mutation Member360AIChat(
    $message: String!
    $memberId: String
    $companyId: String
    $membKeyId: String
    $conversationHistory: [JSON!]
  ) {
    member360AIChat(
      message: $message
      memberId: $memberId
      companyId: $companyId
      membKeyId: $membKeyId
      conversationHistory: $conversationHistory
    ) {
      response
    }
  }
`;

export const SUGGESTED_QUESTIONS = [
  "What changed since this patient's last visit?",
  "Does the patient have any abnormal labs?",
  "What authorizations are currently active?",
  "Is the patient overdue for their AWV?",
];

export interface MemberAssistantContext {
  memberId?: string;
  membKeyId?: string;
  companyId?: string;
}

interface AssistantMessage {
  role: "user" | "assistant";
  content: string;
}

interface SendArgs {
  message: string;
  history: AssistantMessage[];
}

interface Member360AIChatData {
  member360AIChat?: { response?: string } | null;
}

interface Member360AIChatVars {
  message: string;
  memberId?: string;
  membKeyId?: string;
  companyId?: string;
  conversationHistory: AssistantMessage[];
}

export function useMemberAssistantSend(ctx: MemberAssistantContext) {
  const [sendMutation] = useMutation<
    Member360AIChatData,
    Member360AIChatVars
  >(MEMBER_360_AI_CHAT_MUTATION);

  return useCallback(
    async ({ message, history }: SendArgs): Promise<string> => {
      // Emit before the mutation — no PHI in props (message content not included, ACT-1037).
      trackEvent("ai_assistant_message_sent");
      const { data } = await sendMutation({
        variables: {
          message,
          memberId: ctx.memberId,
          membKeyId: ctx.membKeyId,
          companyId: ctx.companyId,
          conversationHistory: history,
        },
      });
      const response = data?.member360AIChat?.response;
      if (!response) throw new Error("No response from assistant.");
      return response;
    },
    [sendMutation, ctx.memberId, ctx.membKeyId, ctx.companyId],
  );
}
